import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Crown } from 'lucide-react';
import { logAnalyticsEvent } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { CheckoutTransition } from '@/components/ui/CheckoutTransition';
import { GlassCard } from '@/components/ui/GlassCard';
import { HeroBackground } from '@/components/three';
import { prefetchCheckoutSubscription } from '@/lib/checkoutPrefetch';
import { PLAN_CONFIGS, PlanId } from '@shared/types';

export function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Every Checkout Session minted before the cancel_url moved to /offer still
  // sends cancellers here. Forward them, so a buyer who backed out never gets
  // shown the same four-tier ladder they just walked away from.
  const canceled = searchParams.get('canceled') === 'true';
  useEffect(() => {
    if (canceled) navigate('/offer?canceled=true', { replace: true });
  }, [canceled, navigate]);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('starter');
  const [isVisible, setIsVisible] = useState(false);
  const [checkoutTransition, setCheckoutTransition] = useState<{ x: number; y: number; planId: PlanId } | null>(null);
  const loading = !!checkoutTransition;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleStartCheckout = (e?: React.MouseEvent) => {
    logAnalyticsEvent('cta_start_today_clicked', {
      location: 'pricing_page',
      plan_id: selectedPlan,
    });
    logAnalyticsEvent('begin_checkout', {
      plan_id: selectedPlan,
      flow: 'guest_embedded',
      currency: 'USD',
      value: PLAN_CONFIGS[selectedPlan].price,
      items: [{ item_id: selectedPlan, item_name: PLAN_CONFIGS[selectedPlan].name, price: PLAN_CONFIGS[selectedPlan].price }],
    });
    // Fire createGuestSubscription in parallel with the 1.3s thanos snap so
    // the clientSecret is ready by the time CheckoutPage mounts. CheckoutPage
    // falls back to its own call if this one fails or is too slow.
    prefetchCheckoutSubscription(selectedPlan).catch(() => {});

    const x = e?.clientX ?? window.innerWidth / 2;
    const y = e?.clientY ?? window.innerHeight / 2;
    setCheckoutTransition({ x, y, planId: selectedPlan });
  };

  const planOrder: PlanId[] = ['starter', 'cashflow', 'scale', 'agency'];

  return (
    <HeroBackground>
      <AnimatePresence>
        {checkoutTransition && (
          <CheckoutTransition
            originX={checkoutTransition.x}
            originY={checkoutTransition.y}
            onComplete={() => navigate(`/checkout?plan=${checkoutTransition.planId}`)}
          />
        )}
      </AnimatePresence>
      <div className="min-h-screen">
        <nav
          className="fixed top-0 left-0 right-0 z-50"
          style={{
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              {/* Identical to the home page nav mark: white border on black, not
                  the mauve gradient tile this had. The wolf is a black-and-white
                  illustration, so the gradient washed a pink square in behind it
                  and it read as a different brand one click from the checkout. */}
              <div className="w-10 h-10 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden">
                <img src="/wolfromulus.png" alt="Romulus" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-2xl font-bold text-white font-roman tracking-wider">ROMVLVS</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Home
              </Link>
              <Link
                to="/pricing"
                className="text-white text-sm"
              >
                Pricing
              </Link>
            </div>

            <Button
              onClick={handleStartCheckout}
              loading={loading}
              className="group"
            >
              <span className="flex items-center gap-2">
                Start today
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </span>
            </Button>
          </div>
        </nav>

        <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 pt-24 pb-12">
          <div
            className={`w-full max-w-7xl transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="text-center mb-12">
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-4"
                style={{
                  textShadow: '0 0 60px rgba(91,58,58,0.3)',
                }}
              >
                Choose your plan
              </h1>

              <p className="text-gray-400 max-w-xl mx-auto">
                Start building your website empire today. All plans include access to every feature.
              </p>
            </div>

            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10"
              style={{
                animation: 'fadeInUp 0.8s ease-out 0.3s both',
              }}
            >
              {planOrder.map((planId) => {
                const plan = PLAN_CONFIGS[planId];
                const isPopular = plan.popular;
                const isSelected = selectedPlan === planId;

                return (
                  <GlassCard
                    key={planId}
                    className={`cursor-pointer transition-all duration-300 transform relative ${
                      isSelected
                        ? 'ring-2 ring-primary-500 border-primary-500/50 shadow-[0_0_40px_rgba(91,58,58,0.3)] scale-[1.02]'
                        : 'hover:border-white/30 hover:scale-[1.01]'
                    } ${isPopular ? 'lg:-mt-4 lg:mb-4' : ''}`}
                    onClick={() => setSelectedPlan(planId)}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="px-3 py-1 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-xs font-semibold text-white shadow-lg flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          MOST POPULAR
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col h-full">
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-white font-display">{plan.name}</h3>
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                              isSelected
                                ? 'bg-primary-500 border-primary-500'
                                : 'border-gray-500 hover:border-primary-400'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 italic">{plan.tagline}</p>
                      </div>

                      <div className="mb-4">
                        <span className="text-4xl font-bold text-white">
                          <span className="text-primary-400 text-2xl">$</span>
                          {plan.price}
                        </span>
                        <span className="text-sm text-gray-400">/month</span>
                      </div>

                      <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-400">Leads</span>
                          <span className="text-white font-semibold">{plan.quotas.leadsLimit.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Websites</span>
                          <span className="text-white font-semibold">{plan.quotas.sitesLimit.toLocaleString()}</span>
                        </div>
                      </div>

                      <ul className="space-y-2 flex-1">
                        {plan.features.slice(2).map((feature, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-gray-300 text-sm"
                          >
                            <div className="w-4 h-4 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-2.5 h-2.5 text-primary-400" />
                            </div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </GlassCard>
                );
              })}
            </div>

            <div
              className="flex flex-col items-center gap-4"
              style={{
                animation: 'fadeInUp 0.8s ease-out 0.5s both',
              }}
            >
              <Button
                size="lg"
                className="w-full max-w-md h-14 text-lg font-semibold group relative overflow-hidden"
                onClick={handleStartCheckout}
                loading={loading}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start with {PLAN_CONFIGS[selectedPlan].name}
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Button>

              <p className="text-gray-500 text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-primary-400 hover:text-primary-300 transition-colors font-medium"
                >
                  Sign in
                </Link>
              </p>

              <p className="text-xs text-gray-500 text-center max-w-md">
                Quotas reset monthly on your billing date. Cancel anytime.
              </p>
            </div>

            <div className="mt-12 text-center">
              <div className="flex items-center justify-center gap-6 opacity-50">
                <div className="text-gray-400 text-xs">HTTPS Secured</div>
                <div className="w-px h-4 bg-gray-700" />
                <div className="text-gray-400 text-xs">Stripe Secure</div>
                <div className="w-px h-4 bg-gray-700" />
                <div className="text-gray-400 text-xs">Cancel Anytime</div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </HeroBackground>
  );
}
